<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { useNotification } from "@ui-kit";
  import { useBarcode, useReadRfid } from "@plugins";
  import { ScanScreen, ScanScreenStart, ProductZone } from "@src/components";
  import { useShopZoneHook } from "@src/hooks";
  import { WTFIRInputType, useGetWTFIR, useLazyProductsLoad, useLazyItemLoadByGuid } from "@src/api";
  import { default as ProductFittingItemZoneList } from "./ProductFittingItemZoneList.vue";

  const emit = defineEmits<{ (e: "close"): void }>();

  const barcode = useBarcode();
  const readRfid = useReadRfid();
  const { t } = useI18n();
  const { show, clear } = useNotification();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getProduct } = useLazyProductsLoad();
  const { execute: getItemLoadByGuid } = useLazyItemLoadByGuid();
  const { shopZones } = useShopZoneHook();

  type ItemZone = {
    id: string;
    article: string;
    img: string;
    isBlocked: boolean;
    zones: { id: string; path: string; location: string }[];
  };
  const itemZoneList = ref<ItemZone[]>([]);
  const isInitialLoad = ref(true);

  const handleScanData = async (data: string, type: WTFIRInputType) => {
    clear();

    const resultWTFIR = await getWTFIR({ data, input_type: type });

    if (resultWTFIR?.isError.value) return;

    const productId = resultWTFIR.data.value.items?.at(0) || null;

    if (!productId) {
      show({ title: t("Товар не найден"), type: "error" });

      return;
    }

    const productResult = await getProduct({ item_id: [productId] });
    const parentProductResult = await getItemLoadByGuid({ guid: productResult.data.value.items.at(0)?.seria_guid });

    if (productResult.isError.value || parentProductResult.isError.value) {
      show({ title: t("Ошибка получения товара"), type: "error" });

      return;
    }

    // TODO: hierarchy
    const item: ItemZone = {
      id: productId,
      article: parentProductResult.data.value.item.article,
      img: parentProductResult.data.value.item.image_url,
      isBlocked: parentProductResult.data.value.item.is_blocked,
      zones: [],
    };
    const zones = parentProductResult.data.value.zones.map((item) => shopZones.value.byId[item.zone_id]);

    if (zones.length > 0) {
      zones.filter(Boolean).forEach((zone) => {
        const zone_ids = zone.hierarchy.split(".");
        let path = "";
        let location = "";

        if (zone_ids.length === 2) {
          path = shopZones.value.byId[zone_ids.at(0)].title;
          location = shopZones.value.byId[zone_ids.at(-1)].title;
        } else if (zone_ids.length > 2) {
          const path_origin = [
            shopZones.value.byId[zone_ids.at(0)].title.slice(0, 1),
            shopZones.value.byId[zone_ids.at(1)].title.slice(-2),
          ].join(" ");
          const path_other = zone_ids.slice(2, -1).map((id) => shopZones.value.byId[id].title);

          path = [path_origin, ...path_other].join(" • ");
          location = shopZones.value.byId[zone_ids.at(-1)].title;
        }

        item.zones.push({
          id: zone_ids.at(-1),
          path,
          location,
        });
      });
    }

    itemZoneList.value.unshift(item);

    if (itemZoneList.value.length > 4) {
      itemZoneList.value.pop();
    }

    isInitialLoad.value = false;
  };

  onMounted(() => {
    barcode.subscribe((data: string) => {
      handleScanData(data, WTFIRInputType.barcode);
    });
    readRfid.subscribe((data) => {
      handleScanData(data.value, WTFIRInputType.rfid);
    });
  });

  onUnmounted(() => {
    barcode.unsubscribe();
    readRfid.unsubscribe();
  });
</script>

<template>
  <div class="widget__product-zoning" data-testid="widget-product-fitting">
    <ScanScreen
      v-if="isInitialLoad"
      data-testid="product-fitting"
      @close="emit('close')"
      :modal="{ title: $t('Примерочные'), subtitle: $t('Отсканируйте товар') }"
    >
      <ScanScreenStart />
    </ScanScreen>
    <ProductFittingItemZoneList v-else @click:back="emit('close')" @click:clear="itemZoneList = []">
      <ProductZone
        v-for="(item, index) in itemZoneList"
        :key="item.id + '-' + index"
        :img="item.img"
        :article="item.article"
        :zones="item.zones"
        :isBlocked="item.isBlocked"
        data-testid="product-fitting-item-zone"
      />
    </ProductFittingItemZoneList>
  </div>
</template>

<style scoped>
  .widget__product-zoning {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 1;
  }
</style>
